/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import filter from 'lodash/filter';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';

import { toZoned } from '@internationalized/date';
import { localNow, localToday } from 'sarsaparilla';
import type { Campground, Season } from '../../../types/camping';
import type { QueueLottery } from '../../../types/lottery';
import { DEFAULT_TIMEZONE } from '../constants';
import {
    isBetweenDates,
    isDateSameOrAfter,
    isDateSameOrBefore,
    UTCStringToCalendarDate,
    UTCStringToZonedDateTime,
} from './dateUtils';

const LOTTERY_SEASON_TYPE = 'Lottery';

export const getEarlyAccessEndOfDay = (earlyAccessEnd: string, timezone: string) => {
    return toZoned(
        UTCStringToCalendarDate(earlyAccessEnd),
        timezone || DEFAULT_TIMEZONE
    ).set({
        hour: 23,
        minute: 59,
        second: 59,
        millisecond: 999,
    });
};

export const isInCampingLotterySeason = (seasons: Season[]) => {
    if (!seasons || isEmpty(seasons)) {
        return false;
    }
    const currentSeason = seasons.find((season) => {
        const startDate = UTCStringToCalendarDate(season.season_start);
        const endDate = UTCStringToCalendarDate(season.season_end).add({ days: 1 });
        return isBetweenDates(localToday, startDate, endDate);
    });

    if (currentSeason && !isEmpty(currentSeason)) {
        return isEqual(currentSeason.season_type, LOTTERY_SEASON_TYPE);
    }
    return false;
};

export const isInEarlyAccessPeriod = (campground: Campground, queueLotteryId: string) => {
    const campgroundTimeZone = campground?.facility_time_zone || DEFAULT_TIMEZONE;
    const queueLottery = campground?.queue_lotteries?.[queueLotteryId];

    if (!queueLottery) {
        return false;
    }

    const { early_access_start: earlyAccessStart, early_access_end: earlyAccessEnd } =
        queueLottery;

    const earlyAccessEndDay = getEarlyAccessEndOfDay(earlyAccessEnd, campgroundTimeZone);

    const earlyAccessStartDay = toZoned(
        UTCStringToCalendarDate(earlyAccessStart),
        campgroundTimeZone
    );

    return (
        isDateSameOrAfter(localNow, earlyAccessStartDay) &&
        isDateSameOrBefore(localNow, earlyAccessEndDay)
    );
};

export const filterActiveEarlyAccessLotteries = (
    queuingLotteryMap: Record<string, QueueLottery>,
    campgroundTimeZone: string
) => {
    return filter(queuingLotteryMap, (queueLottery) => {
        const shouldLotteryBeDisplayed = isDateSameOrAfter(
            localToday,
            UTCStringToZonedDateTime(queueLottery.display_date)
        );
        const { early_access_end: earlyAccessEnd } = queueLottery;

        const earlyAccessEndDay = getEarlyAccessEndOfDay(
            earlyAccessEnd,
            campgroundTimeZone
        );

        return (
            shouldLotteryBeDisplayed && isDateSameOrBefore(localNow, earlyAccessEndDay)
        );
    });
};
